import { useEffect, useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useLoader } from '@/contexts/LoaderContext';
import { useError } from '@/contexts/ErrorContext';

/**
 * Shared internal hook for common loader and error handling logic
 * @private
 * @param {boolean} isLoading - Loading state from query
 * @param {Error} error - Error object from query
 * @param {*} data - Data returned from query
 * @return {Object} Object containing handleError, clearError, loading, notLoading functions
 */
const useQueryEffects = (isLoading, error, data) => {
  const { loading, notLoading } = useLoader();
  const { handleError, clearError } = useError();

  useEffect(() => {
    if (isLoading) {
      loading();
    } else {
      notLoading();
    }

    if (error) {
      handleError(error);
    } else {
      clearError();
    }
  }, [isLoading, error, clearError, data, handleError, loading, notLoading]);

  return { handleError, clearError, loading, notLoading };
};

/**
 * Fetches data for a single query with caching
 * @param {Object} config - Query configuration
 * @param {string|Array} config.currNav - Query key (navigation identifier)
 * @param {Function} config.qryFn - Query function to fetch data
 * @return {Object} { data, error, isLoading }
 */
export const useFetchQuery = ({ currNav, qryFn }) => {
  const queryConfig = {
    queryKey: Array.isArray(currNav) ? currNav : [currNav],
    queryFn: qryFn,
    staleTime: 1000000,
    gcTime: import.meta.env.VITE_CACHE_TIME,
  };

  const { error, isLoading, data } = useQuery(queryConfig);
  useQueryEffects(isLoading, error, data);

  return { data, error, isLoading };
};

useFetchQuery.propTypes = {
  currNav: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  qryFn: PropTypes.func.isRequired,
};

/**
 * Fetches data for multiple parallel queries with caching
 * @param {Array} config - Array of query configurations
 * @param {string|Array} config[].currNav - Query key for each query
 * @param {Function} config[].qryFn - Query function for each query
 * @param {string} config[].key - Unique identifier for result mapping
 * @return {Object} { data, error, isLoading }
 */
export const useFetchQueries = (config) => {
  const { loading, notLoading } = useLoader();
  const { handleError: errorHandler, clearError: clearErr } = useError();

  const isValidConfig = useMemo(() => Array.isArray(config), [config]);

  const queriesConfig = useMemo(() => {
    return isValidConfig
      ? config.map((obj) => ({
          queryKey: Array.isArray(obj.currNav) ? obj.currNav : [obj.currNav],
          queryFn: obj.qryFn,
          staleTime: 1000000,
          gcTime: import.meta.env.VITE_CACHE_TIME,
        }))
      : [];
  }, [config, isValidConfig]);

  const results = useQueries({ queries: queriesConfig });

  const data = useMemo(() => {
    return results
      .map((result, index) => ({
        [config[index].key]: result.data,
      }))
      .filter((item) =>
        Object.values(item).every((value) => value !== undefined),
      );
  }, [results, config]);

  const error = useMemo(
    () => results.find((result) => result.error)?.error,
    [results],
  );

  const isLoading = useMemo(
    () => results.some((result) => result.isLoading),
    [results],
  );

  useEffect(() => {
    if (!isValidConfig) {
      errorHandler(
        new Error('config must be an array of query configurations'),
      );
      return;
    }

    if (isLoading) {
      loading();
    } else {
      notLoading();
    }

    if (error) {
      errorHandler(error);
    } else {
      clearErr();
    }
  }, [
    isValidConfig,
    isLoading,
    error,
    errorHandler,
    clearErr,
    loading,
    notLoading,
  ]);

  return { data, error, isLoading };
};

useFetchQueries.propTypes = {
  config: PropTypes.arrayOf(
    PropTypes.shape({
      currNav: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]).isRequired,
      qryFn: PropTypes.func.isRequired,
      key: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

/**
 * Fetches data for a single query WITHOUT caching
 * Useful for real-time data or data that should always be fresh
 * @param {string|Array} queryKey - Query key
 * @param {Function} queryFn - Query function to fetch data
 * @return {Object} { data, error, isLoading }
 */
export const useFetchQueryNoCache = (queryKey, queryFn) => {
  const queryConfig = {
    queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
    queryFn: queryFn,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  };

  const { error, isLoading, data } = useQuery(queryConfig);
  useQueryEffects(isLoading, error, data);

  return { data, error, isLoading };
};

useFetchQueryNoCache.propTypes = {
  queryKey: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]).isRequired,
  queryFn: PropTypes.func.isRequired,
};

/**
 * Backward compatibility alias for useFetchQuery
 * @deprecated Use useFetchQuery instead
 */
export const useDataCall = useFetchQuery;
